import { ComponentProps, ReactNode } from 'react';
import { Button } from '@givebutter/primitives/button';
import { faThumbTack } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon, FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import * as Tooltip from 'components/tooltip';
import { usePinCampaign } from 'customHooks/usePinCampaign';
import { twMerge } from 'tailwind-merge';
import { Campaign } from 'types/domain';

const CampaignPinActionRoot = ({ children }: { children: ReactNode }) => {
    return (
        <Tooltip.Provider delayDuration={0} disableHoverableContent>
            <Tooltip.Root>{children}</Tooltip.Root>
        </Tooltip.Provider>
    );
};

interface CampaignPinActionTriggerProps extends Omit<ComponentProps<'button'>, 'color'> {
    campaign: Campaign;
    icon?: FontAwesomeIconProps['icon'];
}
const CampaignPinActionTrigger = ({
    campaign,
    disabled,
    children,
    className,
    icon,
    onClick,
    ...restProps
}: CampaignPinActionTriggerProps) => {
    const { mutate, isPending: isLoading } = usePinCampaign();

    if (campaign.type === 'external') {
        return null;
    }

    return (
        <Tooltip.Trigger asChild>
            <Button
                variant="transparent"
                disabled={isLoading || disabled}
                size="sm"
                shape="square"
                className={twMerge(
                    `-mb-1 -mt-1 h-6 w-6 text-gray-500 hover:bg-gray-100`,
                    className,
                )}
                onClick={e => {
                    e.preventDefault();
                    if (!isLoading) {
                        mutate(campaign);
                    }
                    onClick?.(e);
                }}
                {...restProps}
            >
                <FontAwesomeIcon icon={icon || faThumbTack} />
                <span className="sr-only">{children}</span>
            </Button>
        </Tooltip.Trigger>
    );
};

const CampaignPinActionTooltipContent = ({ children }: { children: ReactNode }) => {
    return (
        <Tooltip.Portal>
            <Tooltip.Content inverse>
                <Tooltip.Arrow inverse />
                {children}
            </Tooltip.Content>
        </Tooltip.Portal>
    );
};

const Root = CampaignPinActionRoot;
const Trigger = CampaignPinActionTrigger;
const TooltipContent = CampaignPinActionTooltipContent;

export {
    CampaignPinActionRoot,
    CampaignPinActionTrigger,
    CampaignPinActionTooltipContent,
    //
    Root,
    Trigger,
    TooltipContent,
};

export type { CampaignPinActionTriggerProps };
